import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, GitBranch, Layers, Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusPill, type DeploymentStatus } from "@/components/StatusPill";

interface CustomEnv {
  id: string;
  name: string;
  branch: string;
  createdAt: string;
  url: string;
  latestDeployment: { id: string; status: DeploymentStatus; commitSha: string; createdAt: string } | null;
}

/**
 * 006 US6 — custom environments section (rendered on the Environments tab).
 * A named branch-tracking environment with a stable URL; scoped env vars
 * are created from the Add-variable form below with this env selected.
 */
export function CustomEnvironmentsSection({ orgSlug, projectSlug }: { orgSlug: string; projectSlug: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [branch, setBranch] = useState("");

  const q = useQuery({
    queryKey: ["custom-environments", orgSlug, projectSlug],
    queryFn: () =>
      api
        .get<{ environments: CustomEnv[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/environments`)
        .then((r) => r.environments),
  });

  const create = useMutation({
    mutationFn: () => api.post(`/orgs/${orgSlug}/projects/${projectSlug}/environments`, { name, branch }),
    onSuccess: () => {
      setName("");
      setBranch("");
      qc.invalidateQueries({ queryKey: ["custom-environments", orgSlug, projectSlug] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${orgSlug}/projects/${projectSlug}/environments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-environments", orgSlug, projectSlug] }),
  });

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 text-muted-foreground" /> Custom environments
        </h3>
        <span className="hidden sm:inline text-2xs text-subtle-foreground">
          persistent, branch-tracking, own variables · {q.data?.length ?? 0} / 5
        </span>
      </div>

      {q.isLoading && (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
      )}
      {!q.isLoading && (q.data?.length ?? 0) === 0 && (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          No custom environments. Create one (e.g. <span className="font-mono">staging</span> tracking{" "}
          <span className="font-mono">staging</span>) to get a stable URL that always serves that branch's
          latest build, with its own variables.
        </div>
      )}
      {q.data?.map((e) => (
        <div key={e.id} className="of-row gap-3 items-center">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold">{e.name}</span>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-mono">
                <GitBranch className="h-3 w-3" /> {e.branch}
              </span>
              {e.latestDeployment && <StatusPill status={e.latestDeployment.status} size="xs" />}
            </div>
            <a
              href={e.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 max-w-full min-w-0 text-xs font-mono text-muted-foreground hover:text-foreground mt-0.5 truncate"
            >
              <span className="truncate">{e.url.replace(/^https:\/\//, "")}</span> <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </div>
          <Button
            variant="ghost"
            size="icon"
            title="Delete environment"
            onClick={() => {
              if (window.confirm(`Delete environment "${e.name}"? Its scoped variables and flag values are removed too.`)) {
                del.mutate(e.id);
              }
            }}
          >
            {del.isPending && del.variables === e.id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 text-danger" />
            )}
          </Button>
        </div>
      ))}

      <div className="px-4 py-3 border-t border-border-muted flex items-end gap-2 flex-wrap">
        <div className="space-y-1">
          <Label htmlFor="cenv-name" className="text-xs">Name</Label>
          <Input id="cenv-name" value={name} onChange={(ev) => setName(ev.target.value.toLowerCase())} placeholder="staging" className="h-8 w-40 font-mono" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cenv-branch" className="text-xs">Tracks branch</Label>
          <Input id="cenv-branch" value={branch} onChange={(ev) => setBranch(ev.target.value)} placeholder="staging" className="h-8 w-48 font-mono" />
        </div>
        <Button
          variant="default"
          size="sm"
          disabled={!name || !branch || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create
        </Button>
        {create.error instanceof ApiError && (
          <div className="text-xs text-danger w-full">{create.error.message}</div>
        )}
      </div>
    </div>
  );
}
